import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Activity, Clock, TrendingUp, LayoutDashboard, QrCode, RefreshCw, Copy, X } from "lucide-react";
import { API } from "../../../App";
import { minutesToHHmmInput } from "./diUtils";
import StatusOverview from "./StatusOverview";

function MiniBarChart({ data, valueKey = "total_h", color = "#fb923c" }) {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
  const W = 360, H = 80, PAD = 6;
  const barW = (W - PAD * 2) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[90px]" data-testid="di-bar-chart">
      <line x1={PAD} x2={W - PAD} y1={H - 18} y2={H - 18} stroke="#334155" strokeWidth="0.5" />
      {data.map((d, i) => {
        const v = Number(d[valueKey] || 0);
        const h = Math.round((v / max) * (H - 30));
        const x = PAD + i * barW + 1, y = H - 18 - h;
        return (<g key={d.month}><rect x={x} y={y} width={barW - 2} height={h} rx="1.5" fill={color} opacity="0.85"><title>{`${d.month} — ${v}h`}</title></rect><text x={x + barW / 2 - 1} y={H - 4} textAnchor="middle" fontSize="9" fill="#94a3b8">{d.label}</text></g>);
      })}
    </svg>
  );
}

function MiniLineChart({ data, valueKey = "rolling_12m_total_h", color = "#22d3ee", threshold = 15 }) {
  if (!data?.length) return null;
  const W = 360, H = 80, PAD = 6;
  const max = Math.max(threshold * 1.5, ...data.map((d) => Number(d[valueKey] || 0)));
  const stepX = (W - PAD * 2) / Math.max(1, data.length - 1);
  const yFor = (v) => H - 14 - (Number(v || 0) / max) * (H - 24);
  const points = data.map((d, i) => `${PAD + i * stepX},${yFor(d[valueKey])}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[90px]" data-testid="di-line-chart">
      <line x1={PAD} x2={W - PAD} y1={yFor(threshold)} y2={yFor(threshold)} stroke="#fbbf24" strokeWidth="0.6" strokeDasharray="2 2" />
      <text x={W - PAD - 2} y={yFor(threshold) - 2} textAnchor="end" fontSize="8" fill="#fbbf24">{threshold}h limite</text>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
      {data.map((d, i) => <circle key={d.month} cx={PAD + i * stepX} cy={yFor(d[valueKey])} r="2" fill={color}><title>{`${d.month} — ${d[valueKey]}h rolling`}</title></circle>)}
    </svg>
  );
}

function PublicQrDialog({ instructor, onClose }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrCreate = useCallback(async (rotate = false) => {
    setLoading(true);
    try {
      if (!rotate) {
        const r = await axios.get(`${API}/di/instructors/${instructor.id}`, { withCredentials: true });
        if (r.data?.public_token) { setToken(r.data.public_token); setLoading(false); return; }
      }
      const r2 = await axios.post(`${API}/di/instructors/${instructor.id}/public-token`, {}, { withCredentials: true });
      setToken(r2.data.public_token);
      if (rotate) toast.success("Novo token gerado — QR antigo deixou de funcionar");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a gerar token"); }
    finally { setLoading(false); }
  }, [instructor.id]);

  const revoke = async () => {
    if (!window.confirm("Revogar o QR? Deixará de ser legível imediatamente.")) return;
    try { await axios.delete(`${API}/di/instructors/${instructor.id}/public-token`, { withCredentials: true }); setToken(null); toast.success("QR revogado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  useEffect(() => { fetchOrCreate(false); }, [fetchOrCreate]);

  const baseUrl = (typeof window !== "undefined" && window.location?.origin) || "";
  const publicUrl = token ? `${baseUrl}/di/status/${token}` : null;

  const copy = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); }
    catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-md" data-testid="di-qr-dialog">
        <DialogHeader><DialogTitle className="text-orange-300 flex items-center gap-2"><QrCode className="w-5 h-5" /> QR Público — {instructor.name}</DialogTitle></DialogHeader>
        {loading ? (<div className="py-12 text-center text-slate-500 text-sm">A gerar...</div>)
          : !token ? (<div className="py-8 text-center space-y-3"><div className="text-sm text-slate-300">Sem QR ativo.</div><Button className="btn-cockpit" onClick={() => fetchOrCreate(true)}>Gerar QR</Button></div>)
          : (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded flex items-center justify-center" data-testid="di-qr-image"><QRCodeSVG value={publicUrl} size={220} level="M" includeMargin /></div>
              <div className="text-[11px] text-slate-400 break-all bg-slate-900/50 px-2 py-1.5 rounded border border-cockpit-border">{publicUrl}</div>
              <p className="text-xs text-slate-400 leading-relaxed">O instrutor pode <strong className="text-slate-200">imprimir</strong> este QR. Ao ler, vê o seu semáforo, validades e horas dos últimos 12 meses, sem precisar de iniciar sessão.</p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={copy} data-testid="di-qr-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copiar link</Button>
                <Button size="sm" variant="outline" onClick={() => fetchOrCreate(true)} data-testid="di-qr-rotate"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Rodar token</Button>
                <Button size="sm" variant="ghost" className="text-red-400" onClick={revoke} data-testid="di-qr-revoke"><X className="w-3.5 h-3.5 mr-1" /> Revogar</Button>
              </div>
            </div>
          )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Fechar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InstructorDashboardDialog({ open, instructor, asOf: initialAsOf, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [asOf, setAsOf] = useState(initialAsOf || "");

  const refresh = useCallback(async () => {
    if (!open || !instructor) return;
    setLoading(true);
    try {
      const params = asOf ? { as_of: asOf } : {};
      const r = await axios.get(`${API}/di/instructors/${instructor.id}/dashboard`, { withCredentials: true, params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a carregar dashboard"); }
    finally { setLoading(false); }
  }, [open, instructor, asOf]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!instructor) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-cockpit-panel border-cockpit-border w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="di-dashboard-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-300 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><LayoutDashboard className="w-5 h-5" /> Dashboard — {instructor.name} (PU {instructor.pu_number})</span>
            <Button size="sm" variant="outline" onClick={() => setShowQr(true)} data-testid="di-qr-open-btn" title="QR público para o instrutor consultar o seu semáforo">
              <QrCode className="w-4 h-4 mr-1" /> QR Público
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-12 text-center text-slate-500 text-sm">A carregar...</div>
        ) : (
          <div className="space-y-4">
            <StatusOverview detail={{ ...data, id: instructor.id }} asOf={asOf} setAsOf={setAsOf} onApply={refresh} onChanged={() => { onChanged?.(); refresh(); }} />
            <details className="rounded border border-slate-700/60 bg-slate-900/40" data-testid="di-section-charts">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-200 flex items-center justify-between gap-2">
                Horas e tendência (últimos 12 meses)
                <span className="text-[11px] font-normal text-slate-400">2 gráficos</span>
              </summary>
              <div className="p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="instrument-panel">
                <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Horas voadas — últimos 12 meses</CardTitle></CardHeader>
                <CardContent>
                  <MiniBarChart data={data.monthly_series} valueKey="total_h" color="#fb923c" />
                  <div className="text-[10px] text-slate-500 mt-1">Soma 12m: {minutesToHHmmInput(data.hours?.last12m_total_min ?? 0)}</div>
                </CardContent>
              </Card>
              <Card className="instrument-panel">
                <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Tendência rolling 12m (vs limite ANAC)</CardTitle></CardHeader>
                <CardContent>
                  <MiniLineChart data={data.monthly_series} valueKey="rolling_12m_total_h" color="#22d3ee" threshold={15} />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Atual: {minutesToHHmmInput(data.hours?.last12m_total_min ?? 0)} / 15:00 requeridas
                    {data.hours?.last12m_total_h >= 15 && data.hours?.last12m_total_h < 18 && <span className="text-amber-400/80 ml-1.5">(margem reduzida)</span>}
                  </div>
                </CardContent>
              </Card>
            </div>
              </div>
            </details>
            <details className="rounded border border-slate-700/60 bg-slate-900/40" data-testid="di-section-countdown">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-200 flex items-center justify-between gap-2">
                Próximas validades
                <span className="text-[11px] font-normal text-slate-400">datas-limite</span>
              </summary>
              <div className="p-2">
            <Card className="instrument-panel">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Próximas validades</CardTitle></CardHeader>
              <CardContent>
                {data.countdowns?.length ? (
                  <div className="space-y-1.5" data-testid="di-countdown-list">
                    {data.countdowns.map((c, i) => {
                      const d = c.days_remaining;
                      const tone = d == null ? "slate" : d < 0 ? "red" : d <= 45 ? "amber" : "emerald";
                      const cls = { red: "border-red-500/40 bg-red-500/10 text-red-300", amber: "border-amber-500/40 bg-amber-500/10 text-amber-300", emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300", slate: "border-slate-700 bg-slate-800/30 text-slate-300" }[tone];
                      return (<div key={i} className={`flex items-center justify-between gap-2 px-3 py-2 rounded border ${cls}`}><span className="text-xs font-medium">{c.label}</span><span className="font-mono text-xs">{c.target_date}{d != null && <span className="ml-2 opacity-80">({d >= 0 ? `${d}d` : `expirou há ${-d}d`})</span>}</span></div>);
                    })}
                  </div>
                ) : <div className="text-xs text-slate-500 italic">Sem validades registadas.</div>}
              </CardContent>
            </Card>
              </div>
            </details>
          </div>
        )}
        {showQr && <PublicQrDialog instructor={instructor} onClose={() => setShowQr(false)} />}
      </DialogContent>
    </Dialog>
  );
}
